import classNames from 'classnames'
import React, { PropsWithChildren } from 'react'

import { BaseComponentProps } from '@ui/common/component.props'
import { IconRenderer, IconRendererType } from '@ui/icons/utils/IconRenderer'

import styles from './IconButton.module.scss'

export interface IconButtonProps
  extends Omit<BaseComponentProps<HTMLButtonElement>, 'children' | 'className'> {
  variant?: IconButtonVariant
  size?: IconButtonSize
  disabled?: boolean
  icon: IconRendererType
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
  onDisabledClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
  'data-action'?: string
}

type IconButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'
type IconButtonVariant = 'primary' | 'secondary' | 'ghost' | 'tertiary' | 'brand' | 'dark'

type IconButtonVariantProps = Omit<IconButtonProps, 'variant'>

const IconButtonRoot: React.FC<IconButtonProps> = (props) => {
  const {
    size = 'md',
    variant = 'primary',
    disabled = false,
    icon,
    onClick,
    onDisabledClick,
    ...restProps
  } = props

  const iconButtonClasses = classNames(
    styles.button,
    styles[`size-${size}`],
    styles[`variant-${variant}`],
    {
      [styles.stateDisabled]: disabled,
    }
  )

  const iconSize = getIconSize(size)

  const handleInteraction = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) {
      event.preventDefault()

      if (disabled && onDisabledClick) onDisabledClick(event)
      return
    }

    if (onClick) onClick(event)
  }

  return (
    <button
      className={iconButtonClasses}
      aria-disabled={disabled}
      onClick={handleInteraction}
      type='button'
      {...restProps}
    >
      <span className={styles.icon}>
        <IconRenderer icon={icon} size={iconSize} />
      </span>
    </button>
  )
}

const getIconSize = (size: IconButtonSize): number => {
  return {
    xs: 12,
    sm: 16,
    md: 20,
    lg: 20,
    xl: 24,
  }[size]
}

export const IconButton = Object.assign(IconButtonRoot, {
  Primary: (props: PropsWithChildren<IconButtonVariantProps>) => (
    <IconButtonRoot
      {...props}
      variant='primary'
      icon={{ icon: props.icon, color: 'currentColor' } as IconRendererType}
    />
  ),
  Secondary: (props: PropsWithChildren<IconButtonVariantProps>) => (
    <IconButtonRoot {...props} variant='secondary' />
  ),
  Brand: (props: PropsWithChildren<IconButtonVariantProps>) => (
    <IconButtonRoot {...props} variant='brand' />
  ),
  Ghost: (props: PropsWithChildren<IconButtonVariantProps>) => (
    <IconButtonRoot {...props} variant='ghost' />
  ),
  Tertiary: (props: PropsWithChildren<IconButtonVariantProps>) => (
    <IconButtonRoot {...props} variant='tertiary' />
  ),
  Dark: (props: PropsWithChildren<IconButtonVariantProps>) => (
    <IconButtonRoot {...props} variant='dark' />
  ),
})
